import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [contactReason, setContactReason] = useState('general');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setContactReason('general');
    }, 4000);
  };

  const contactReasons = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'sales', label: 'Sales & Pricing' },
    { value: 'support', label: 'Technical Support' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'demo', label: 'Request Demo' },
  ];

  const contactMethods = [
    {
      icon: 'mail',
      title: 'Email Us',
      primary: 'hello@orbitmind.ai',
      secondary: 'support@orbitmind.ai',
      gradient: 'linear-gradient(135deg, #13a4ec, #0e7fc0)',
      glow: 'rgba(19,164,236,0.3)',
    },
    {
      icon: 'phone',
      title: 'Call Us',
      primary: '+1 (555) 123-4567',
      secondary: 'Mon-Fri 9AM-6PM EST',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      glow: 'rgba(16,185,129,0.3)',
    },
    {
      icon: 'chat',
      title: 'Live Chat',
      primary: 'Available 24/7',
      secondary: 'Avg response: 2 min',
      gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
      glow: 'rgba(124,58,237,0.3)',
    },
    {
      icon: 'location_on',
      title: 'Visit Us',
      primary: '123 Innovation Drive',
      secondary: 'San Francisco, CA',
      gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
      glow: 'rgba(249,115,22,0.3)',
    },
  ];

  const faqs = [
    {
      q: 'How quickly do you respond?',
      a: 'Within 24 hours on business days. Urgent support requests are handled within 4 hours.',
    },
    {
      q: 'Do you offer custom AI solutions?',
      a: 'Yes — we specialize in custom AI workflows and automation pipelines tailored to your specific business needs.',
    },
    {
      q: 'Can I schedule a demo?',
      a: 'Absolutely. Contact us through the form or email us directly to schedule a personalized demo of the platform.',
    },
    {
      q: 'What technical support do you provide?',
      a: 'We provide full-cycle support: setup, troubleshooting, best practices guidance, and ongoing optimization.',
    },
  ];

  return (
    <div
      className="min-h-screen font-display"
      style={{ backgroundColor: '#101c22', color: '#fff' }}
    >
      {/* Announcement Bar */}
      <div
        className="w-full text-center py-2 text-sm font-medium"
        style={{ backgroundColor: '#13a4ec', color: '#fff' }}
      >
        🚀 OrbitMind v2.0 is live — now with multi-agent orchestration.{' '}
        <Link to="/announcement" className="underline font-bold hover:opacity-80 transition-opacity">
          See what's new →
        </Link>
      </div>

      {/* Navbar */}
      <Navbar variant="transparent" />

      {/* ── Hero ── */}
      <section className="relative py-28 overflow-hidden">
        {/* Mesh grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Orbs */}
        <div
          className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: 0.18,
          }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: 0.15,
          }}
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm font-medium text-gray-300 mb-8">
            <span className="material-symbols-outlined text-base" style={{ color: '#13a4ec' }}>
              contact_support
            </span>
            Contact Us
          </div>

          <h1 className="text-6xl font-extrabold leading-tight mb-6">
            Let's build something{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              extraordinary.
            </span>
          </h1>

          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Whether you're ready to automate or just exploring — we're here to help.
          </p>

          {/* Contact pills */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {[
              { emoji: '📧', text: 'hello@orbitmind.ai' },
              { emoji: '📞', text: '+1 (555) 123-4567' },
              { emoji: '💬', text: 'Live Chat 24/7' },
            ].map((pill) => (
              <div
                key={pill.text}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/5 text-sm text-gray-300 hover:border-white/20 hover:bg-white/10 transition-all cursor-pointer"
              >
                <span>{pill.emoji}</span>
                <span>{pill.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact Methods Grid ── */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactMethods.map((method) => (
            <div
              key={method.title}
              className="rounded-2xl p-8 border border-white/10 transition-all duration-300 hover:-translate-y-1 hover:border-white/20 cursor-pointer"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: method.gradient, boxShadow: `0 0 20px ${method.glow}` }}
              >
                <span className="material-symbols-outlined text-white text-xl">{method.icon}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">{method.title}</h3>
              <p className="text-white font-medium text-sm mb-1">{method.primary}</p>
              <p className="text-gray-400 text-sm">{method.secondary}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Form + FAQ ── */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Left — Contact Form */}
          <div
            className="rounded-2xl border border-white/10 p-8"
            style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
          >
            <h2 className="text-2xl font-extrabold text-white mb-2">Send us a message</h2>
            <p className="text-gray-400 text-sm mb-8">We'll get back to you within 24 hours.</p>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                  style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                >
                  <span className="material-symbols-outlined text-white text-3xl">check_circle</span>
                </div>
                <h3 className="text-2xl font-extrabold text-white mb-2">Message Sent!</h3>
                <p className="text-gray-400">Thank you for reaching out. We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Contact reason pills */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    What can we help you with?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {contactReasons.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setContactReason(r.value)}
                        className="px-4 py-2 rounded-full text-sm font-medium border transition-all"
                        style={
                          contactReason === r.value
                            ? {
                                background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                                borderColor: 'transparent',
                                color: '#fff',
                              }
                            : {
                                backgroundColor: 'transparent',
                                borderColor: 'rgba(255,255,255,0.15)',
                                color: '#9ca3af',
                              }
                        }
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name <span style={{ color: '#13a4ec' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm"
                      style={{ '--tw-ring-color': '#13a4ec' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email <span style={{ color: '#13a4ec' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>
                </div>

                {/* Company + Subject */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subject <span style={{ color: '#13a4ec' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="How can we help?"
                      className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message <span style={{ color: '#13a4ec' }}>*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us more about your inquiry..."
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm resize-none"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 hover:scale-[1.01]"
                  style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                >
                  <span>Send Message</span>
                  <span className="material-symbols-outlined text-base">send</span>
                </button>
              </form>
            )}
          </div>

          {/* Right — FAQ */}
          <div>
            <h2 className="text-2xl font-extrabold text-white mb-2">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-sm mb-8">Quick answers to common questions.</p>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 overflow-hidden transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-white text-sm pr-4">{faq.q}</span>
                    <span
                      className="material-symbols-outlined text-gray-400 flex-shrink-0 transition-transform duration-300"
                      style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      expand_more
                    </span>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: openFaq === i ? '200px' : '0px' }}
                  >
                    <p className="px-6 pb-5 text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Extra info card */}
            <div
              className="mt-8 rounded-2xl border border-white/10 p-6"
              style={{ backgroundColor: 'rgba(19,164,236,0.06)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                >
                  <span className="material-symbols-outlined text-white text-base">support_agent</span>
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Still have questions?</h4>
                  <p className="text-gray-400 text-sm">
                    Our support team is available 24/7. Reach us via live chat for the fastest response.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactPage;
